<script setup>
import { computed, ref, watch } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useFunctionGetter } from 'dashboard/composables/store';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import { useMessageContext } from '../../provider.js';

defineProps({
  section: {
    type: String,
    required: true,
    validator: value => ['header', 'footer'].includes(value),
  },
});

const { additionalAttributes, inboxId } = useMessageContext();
const whatsAppTemplates = useFunctionGetter(
  'inboxes/getWhatsAppTemplates',
  inboxId
);
const imageLoadFailed = ref(false);

const templateParams = computed(() => {
  return (
    additionalAttributes.value?.templateParams ||
    additionalAttributes.value?.template_params ||
    {}
  );
});

const processedParams = computed(() => {
  return (
    templateParams.value?.processedParams ||
    templateParams.value?.processed_params ||
    {}
  );
});

const storedComponents = computed(() => {
  const components = templateParams.value?.components;
  return Array.isArray(components) ? components : [];
});

const matchingTemplate = computed(() => {
  const templates = whatsAppTemplates.value;
  const name = templateParams.value?.name;
  const language = templateParams.value?.language?.toLowerCase();

  if (!Array.isArray(templates) || !name) return null;

  return (
    templates.find(
      template =>
        template.name === name &&
        (!language || template.language?.toLowerCase() === language)
    ) ||
    templates.find(template => template.name === name) ||
    null
  );
});

const components = computed(() => {
  if (storedComponents.value.length) return storedComponents.value;
  return matchingTemplate.value?.components || [];
});

const getComponent = type => {
  return findComponentByType({ components: components.value }, type);
};

const headerComponent = computed(() => getComponent(COMPONENT_TYPES.HEADER));
const footerComponent = computed(() => getComponent(COMPONENT_TYPES.FOOTER));
const buttonComponent = computed(() => getComponent(COMPONENT_TYPES.BUTTONS));

const getValue = (object, keys) => {
  return keys.reduce((found, key) => {
    if (found !== '') return found;
    const value = object?.[key];
    return value === undefined || value === null ? '' : value;
  }, '');
};

const toCamelKey = key =>
  key.replace(/_([a-z])/g, (_, character) => character.toUpperCase());

const getParamValue = (params, key) => {
  const value = params?.[key] ?? params?.[toCamelKey(key)];
  return value === undefined || value === null || value === '' ? null : value;
};

const replaceTemplateText = (text = '', params = {}) => {
  return text.replace(/{{([^}]+)}}/g, (match, variable) => {
    const value = getParamValue(params, variable.trim());
    return value === null ? match : value;
  });
};

const isHttpUrl = value => {
  if (!value) return false;

  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
};

const fileNameFromUrl = value => {
  try {
    const pathname = new URL(value).pathname;
    return decodeURIComponent(pathname.split('/').filter(Boolean).pop() || '');
  } catch {
    return '';
  }
};

const headerParams = computed(() => processedParams.value?.header || {});
const footerParams = computed(() => processedParams.value?.footer || {});

const rawMediaUrl = computed(() =>
  getValue(headerParams.value, ['media_url', 'mediaUrl'])
);

const mediaUrl = computed(() =>
  isHttpUrl(rawMediaUrl.value) ? rawMediaUrl.value : ''
);

watch(mediaUrl, () => {
  imageLoadFailed.value = false;
});

const mediaType = computed(() => {
  const type =
    getValue(headerParams.value, ['media_type', 'mediaType']) ||
    headerComponent.value?.format ||
    '';
  return String(type).toLowerCase();
});

const normalizedMediaType = computed(() => {
  return ['image', 'video', 'document'].includes(mediaType.value)
    ? mediaType.value
    : 'document';
});

const mediaLabel = computed(() => {
  return (
    getValue(headerParams.value, ['media_name', 'mediaName']) ||
    fileNameFromUrl(mediaUrl.value) ||
    mediaUrl.value
  );
});

const isMediaHeader = computed(() => {
  const format = headerComponent.value?.format || '';
  return MEDIA_FORMATS.includes(format) || Boolean(mediaUrl.value);
});

const headerText = computed(() => {
  if (isMediaHeader.value) return '';
  return replaceTemplateText(
    headerComponent.value?.text || '',
    headerParams.value
  );
});

const footerText = computed(() => {
  return replaceTemplateText(
    footerComponent.value?.text || '',
    footerParams.value
  );
});

const buttonIcon = type => {
  const icons = {
    COPY_CODE: 'i-lucide-copy',
    PHONE_NUMBER: 'i-lucide-phone',
    QUICK_REPLY: 'i-lucide-message-square',
    URL: 'i-lucide-external-link',
  };

  return icons[type] || 'i-lucide-square';
};

const buttons = computed(() => {
  const templateButtons = buttonComponent.value?.buttons || [];

  return templateButtons
    .map((button, index) => {
      const type = (button.type || '').toUpperCase();
      const text =
        button.text ||
        button.phone_number ||
        button.phoneNumber ||
        button.url ||
        '';

      return {
        key: `${type}-${text || index}`,
        icon: buttonIcon(type),
        text,
      };
    })
    .filter(button => button.text);
});

const hasHeaderPreview = computed(() =>
  Boolean(headerText.value || mediaUrl.value)
);
const hasFooterPreview = computed(() =>
  Boolean(footerText.value || buttons.value.length)
);
</script>

<template>
  <template v-if="section === 'header' && hasHeaderPreview">
    <p
      v-if="headerText"
      class="text-sm font-semibold leading-5 whitespace-pre-wrap break-words"
    >
      {{ headerText }}
    </p>

    <a
      v-else-if="normalizedMediaType === 'image' && !imageLoadFailed"
      :href="mediaUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="block overflow-hidden rounded-lg bg-n-alpha-2 skip-context-menu"
    >
      <img
        :src="mediaUrl"
        :alt="mediaLabel"
        class="object-cover w-full rounded-lg max-h-64"
        loading="lazy"
        @error="imageLoadFailed = true"
      />
    </a>

    <video
      v-else-if="normalizedMediaType === 'video'"
      :src="mediaUrl"
      controls
      preload="metadata"
      class="w-full rounded-lg bg-n-alpha-2 max-h-64 skip-context-menu"
    />

    <a
      v-else
      :href="mediaUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="flex gap-2 items-center px-3 py-2 min-w-0 rounded-lg bg-n-alpha-2 hover:bg-n-alpha-3 skip-context-menu"
    >
      <Icon icon="i-lucide-file-text" class="shrink-0 size-4" />
      <span class="min-w-0 truncate">{{ mediaLabel }}</span>
    </a>
  </template>

  <div
    v-else-if="section === 'footer' && hasFooterPreview"
    class="flex flex-col gap-2"
  >
    <p
      v-if="footerText"
      class="mb-0 text-xs leading-5 opacity-75 whitespace-pre-wrap break-words"
    >
      {{ footerText }}
    </p>

    <div v-if="buttons.length" class="flex flex-col gap-1.5">
      <span
        v-for="button in buttons"
        :key="button.key"
        class="inline-flex gap-1.5 justify-center items-center px-3 py-1.5 max-w-full text-xs font-medium rounded-md border bg-n-alpha-2 border-n-alpha-3"
      >
        <Icon :icon="button.icon" class="shrink-0 size-3.5" />
        <span class="min-w-0 truncate">{{ button.text }}</span>
      </span>
    </div>
  </div>
</template>
